import { supabase } from './supabase';

export interface ENumber {
  id: string;
  e_code: string;
  name: string;
  category: string;
  status: 'halal' | 'haram' | 'mushbooh';
  source: string | null;
  description: string | null;
}

// Cache configuration
const CACHE_KEY = 'e_numbers_cache';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

interface CacheEntry {
  timestamp: number;
  data: ENumber[];
}

// Get all E-numbers from cache or database
export async function getAllENumbers(): Promise<ENumber[]> {
  // Check cache first
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    const { timestamp, data }: CacheEntry = JSON.parse(cached);
    if (Date.now() - timestamp < CACHE_DURATION) {
      return data;
    }
  }

  // Fetch from Supabase if cache miss or expired
  const { data, error } = await supabase
    .from('e_numbers')
    .select('*')
    .order('e_code');

  if (error) {
    console.error('Error fetching E-numbers:', error);
    throw new Error('Failed to fetch E-numbers');
  }

  // Update cache
  localStorage.setItem(CACHE_KEY, JSON.stringify({
    timestamp: Date.now(),
    data
  }));

  return data;
}

// Extract food additive numbers from ingredient text
export function extractENumbers(ingredient: string): string[] {
  // Match both E-numbers and plain numbers
  const eNumberRegex = /\b(?:E-?)?(\d{3,4}[a-z]?)\b/gi;
  const matches = ingredient.match(eNumberRegex) || [];
  
  return matches.map(code => {
    // If it starts with 'E' or 'e', standardize the format
    if (/^e-?/i.test(code)) {
      return code.toUpperCase().replace(/^E-?/, 'E');
    }
    // Otherwise, return just the number
    return code.toUpperCase();
  });
}

// Analyze ingredients for food additive numbers
export async function analyzeIngredients(ingredients: string[]): Promise<{
  haramENumbers: ENumber[];
  mushboohENumbers: ENumber[];
  allENumbers: ENumber[];
}> {
  const eNumbers = await getAllENumbers();
  const eNumberMap = new Map(eNumbers.map(e => [e.e_code.replace(/^E/, ''), e]));
  
  const foundENumbers = new Set<ENumber>();
  ingredients.forEach(ingredient => {
    const codes = extractENumbers(ingredient);
    codes.forEach(code => {
      // Remove E prefix if present for lookup
      const numberOnly = code.replace(/^E/, '');
      const eNumber = eNumberMap.get(numberOnly);
      if (eNumber) {
        foundENumbers.add(eNumber);
      }
    });
  });

  const matchedENumbers = Array.from(foundENumbers);

  return {
    haramENumbers: matchedENumbers.filter(e => e.status === 'haram'),
    mushboohENumbers: matchedENumbers.filter(e => e.status === 'mushbooh'),
    allENumbers: matchedENumbers
  };
}